import React from 'react';
import { FormControl, TextField } from '@material-ui/core';
import ReactPlaceholder from 'react-placeholder/lib/ReactPlaceholder';
import classNames from 'clsx';

import { ControlContainer } from 'app/components/ControlContainer';
import { UtilService } from 'app/services/util.service';
import { SelectControlProps } from './SelectControl.interfaces';
import isLabelFitToInput from 'app/shared/helpers/isLabelFitToInput';
import { InputPlaceholder } from 'app/components/InputPlaceholder';
import { FieldsEnum, LabelEnum } from 'app/shared/enums';
import { EndAdornment } from 'app/components/EndAdornment';
import { useSelectControl } from 'app/shared/hooks/controls/useSelectControl';
import { useStyles } from './SelectControl.styles';
import { DoneAdornment } from 'app/components/DoneAdornment';

export default function SelectControl(props: SelectControlProps) {
  const classes = useStyles();

  const {
    className,
    defaultValue,
    helperText,
    input,
    isLoading,
    native,
    tooltip,
    width,
    margin,
    placeholder,
  } = props;

  const {
    error,
    focused,
    getHasDependsOnWithoutValue,
    getHasDictionaryWithoutData,
    getLabel,
    handleBlur,
    handleChange,
    handleFocus,
    isDisabled,
    readOnly,
    renderMenuItems,
    renderNativeItems,
    setDefault,
    valid,
    getDictionaryValue,
  } = useSelectControl(props);

  const inputValue = isDisabled()
    ? ''
    : readOnly
    ? getDictionaryValue(input.value)
    : input.value;

  const shouldDisplayLabel =
    !(native && !!placeholder) ||
    inputValue !== '' ||
    input?.name === FieldsEnum.CarLeasingVehicleType;

  const renderControl = () => {
    const menuProps = {
      classes: {
        paper: classes.selectMenu,
      },
      MenuListProps: {
        'aria-label': 'select options',
        classes: {
          root: classes.menuListItem,
        },
      },
      anchorOrigin: {
        vertical: 'bottom' as 'bottom',
        horizontal: 'left' as 'left',
      },
      getContentAnchorEl: null,
    };

    if (!input.value && defaultValue) {
      setDefault();
    }

    return (
      <TextField
        id={input.name}
        inputProps={{
          'aria-labelledby': `${input.name}-label`,
          tabIndex: readOnly ? -1 : 0,
        }}
        classes={{
          root: classNames(classes.root, {
            [classes.unselected]: inputValue === '',
            [classes.readOnly]: readOnly,
          }),
        }}
        variant={'outlined'}
        margin={margin || 'dense'}
        select={!readOnly}
        InputProps={{
          readOnly: readOnly,
          startAdornment: valid &&
            !focused &&
            UtilService.hasValue(input.value) &&
            !isDisabled() && <DoneAdornment />,
          endAdornment: !readOnly && <EndAdornment open={focused} />,
          classes: {
            adornedEnd: classes.adorendEnd,
          },
        }}
        SelectProps={{
          displayEmpty: !native && !!placeholder,
          native,
          MenuProps: menuProps,
          classes: {
            icon: classes.selectChevron,
            select: classes.select,
          },
          ...(!native &&
            !!placeholder && {
              renderValue: (selected: string) => {
                const content = selected ? (
                  getDictionaryValue(selected)
                ) : (
                  <p className={classes.placeholder}>{placeholder}</p>
                );

                return content;
              },
            }),
        }}
        {...input}
        fullWidth
        value={inputValue}
        defaultValue={undefined}
        label={
          isLabelFitToInput(getLabel(), width) && shouldDisplayLabel
            ? getLabel()
            : ''
        }
        disabled={isDisabled()}
        onChange={handleChange}
        onClick={!readOnly ? handleFocus : undefined}
        onBlur={handleBlur}
        error={error}
        InputLabelProps={{
          shrink: focused || UtilService.hasValue(input.value) || !!placeholder,
          id: `${input.name}-label`,
        }}
      >
        {native ? renderNativeItems() : renderMenuItems()}
      </TextField>
    );
  };

  return (
    <ReactPlaceholder
      ready={!isLoading}
      customPlaceholder={<InputPlaceholder width={width} />}
    >
      <FormControl className={className} fullWidth disabled={isDisabled()}>
        <ControlContainer
          inputId={input.name}
          tooltip={tooltip}
          width={width}
          focused={focused}
          floated={focused || UtilService.hasValue(input.value)}
          helperText={helperText}
          label={!isLabelFitToInput(getLabel(), width) ? getLabel() : ''}
          hoverTooltipText={
            getHasDependsOnWithoutValue() || getHasDictionaryWithoutData()
              ? LabelEnum.FillPreviousQuestions
              : ''
          }
        >
          {renderControl()}
        </ControlContainer>
      </FormControl>
    </ReactPlaceholder>
  );
}
